import React from 'react';
import { Link, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Radio, Calendar, Users, LogOut, Loader2, Shield, MessageSquarePlus, Mic, Settings, Inbox, ShieldAlert } from 'lucide-react';

const navItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/admin/episodes', label: 'Episodes', icon: Mic },
  { path: '/admin/shows', label: 'Shows', icon: Radio },
  { path: '/admin/schedule', label: 'Schedule', icon: Calendar },
  { path: '/admin/hosts', label: 'Hosts & DJs', icon: Users },
  { path: '/admin/requests', label: 'Song Requests', icon: MessageSquarePlus },
  { path: '/admin/submissions', label: 'Submissions', icon: Inbox },
  { path: '/admin/takedowns', label: 'Takedowns', icon: ShieldAlert },
  { path: '/admin/users', label: 'Admin Users', icon: Shield },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex items-center justify-center">
        <Loader2 size={32} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#0F1115]">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A0C10] border-r border-white/5 h-screen fixed left-0 top-0 hidden lg:flex flex-col z-50">
        <div className="p-6 border-b border-white/5">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#D4AF37] rounded-lg flex items-center justify-center">
              <Radio size={18} className="text-[#0F1115]" />
            </div>
            <span className="text-lg font-semibold text-white tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Christovibes</span>
          </Link>
          <p className="text-[#94A3B8] text-xs mt-2">Admin Panel</p>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                    : 'text-[#94A3B8] hover:text-white hover:bg-white/5'
                }`}
              >
                <item.icon size={18} /> {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
              <span className="text-[#D4AF37] text-sm font-semibold">{user?.name?.[0] || 'A'}</span>
            </div>
            <div className="min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-[#94A3B8] text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="flex items-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm text-[#94A3B8] hover:text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden sticky top-0 bg-[#0A0C10] border-b border-white/5 z-40 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#D4AF37] rounded-lg flex items-center justify-center">
              <Radio size={14} className="text-[#0F1115]" />
            </div>
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>Christovibes Admin</span>
          </Link>
          <button onClick={logout} className="p-2 rounded-lg hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400">
            <LogOut size={16} />
          </button>
        </div>
        <div className="flex gap-1 overflow-x-auto pb-1">
          {navItems.map(item => {
            const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 transition-all ${
                  isActive ? 'bg-[#D4AF37] text-[#0F1115]' : 'bg-white/5 text-[#94A3B8]'
                }`}
              >
                <item.icon size={12} /> {item.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* Main Content */}
      <main className="lg:pl-64 min-h-screen pb-8">
        <div className="p-6 sm:p-8 max-w-6xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
